"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { cubicBezier, useTransform, type MotionValue } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { MotionDiv } from "@/components/global/utils/motionTags";
import { promoteLayer } from "@/components/global/utils/motionLayer";
import { useMotionMode } from "@/components/global/utils/motionMode";
import { useReveal, useRevealProgress } from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

/**
 * How far right the photo sits to read as centred before it travels left into its
 * Figma position: half the space the principles column leaves behind. The row is
 * 196rem with 1.25rem of padding either side, and the photo is 127.875rem of that
 * 193.5rem content box, so (193.5 - 127.875) / 2 lands it dead centre — and since
 * the row is itself centred in the container, centred in the section too.
 */
const PHOTO_CENTRE_OFFSET = 32.8125;

/**
 * The row is scrubbed across its own height plus a slice of the viewport rather
 * than the per-block default's fixed 40vh.
 *
 * That default is sized for a block that only fades and rises 2.5rem. This row
 * asks for far more in the same scroll: the photo travels 32.8rem sideways
 * while the principles cascade in behind it. Spanning the row itself roughly
 * doubles the scroll the sequence has to play in, which is the difference
 * between a move you follow and one that is over before you have looked at it.
 */
const ROW_OFFSET = ["start 0.9", "end 0.6"];

/**
 * The photo travels on its own curve instead of the shared REVEAL_EASE.
 *
 * REVEAL_EASE is expo-out: 76% of the distance is covered in the first quarter
 * of the window. On a fade or a 2.5rem rise that reads as crisp, but over
 * 32.8rem it is a lunge followed by a crawl — the photo is essentially in place
 * before you have scrolled far enough to register it moving. This is still an
 * ease-out, so the photo settles rather than stopping dead, but it spends the
 * window close to evenly.
 */
const PHOTO_EASE = cubicBezier(0.25, 0.46, 0.45, 0.94);

function Principle({
  item,
  progress,
  index,
  total,
  reduceMotion,
}: {
  item: PageBastianKellerSchema["missionItems"][number];
  progress: MotionValue<number>;
  index: number;
  total: number;
  reduceMotion: boolean;
}) {
  const { reveal, y } = useReveal(progress, index, total, reduceMotion);

  return (
    <MotionDiv
      style={{ opacity: reveal, y }}
      className="bg-[#112628] px-[2.5rem] py-[1.875rem] [clip-path:polygon(0_0,100%_0,100%_calc(100%-3.589rem),calc(100%-3.589rem)_100%,0_100%)]">
      <div className="richtext richtext-start prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.42] prose-p:text-white/60 lg:prose-p:leading-[1.5] prose-strong:font-normal prose-strong:text-white/80">
        <PortableText
          value={item?.text}
          components={richTextComponent}
        />
      </div>
    </MotionDiv>
  );
}

export default function MissionSection({
  data,
  className,
}: {
  data: PageBastianKellerSchema;
  className?: string;
}) {
  const rowRef = React.useRef<HTMLDivElement>(null);

  // below lg the photo stacks above the principles rather than sitting beside
  // them, so there is no vacated space to travel into
  const { reduceMotion, parallax: slide } = useMotionMode();

  const progress = useRevealProgress(rowRef, ROW_OFFSET, reduceMotion);

  const principles = data?.missionItems;
  // the photo takes the first slice, each principle one of the rest
  const total = 1 + (principles?.length ?? 0);

  const photo = useReveal(progress, 0, total, reduceMotion);
  /*
    The photo travels left off the principles rather than rising into place, so it
    maps its slice to x instead of using the shared rise. It rides `linear` under
    PHOTO_EASE rather than the pre-eased `reveal`: opacity keeps the sharp shared
    curve so the photo is solid early, and only the travel is slowed down.

    Because the slices overlap, the first principle is already fading up while the
    photo is still running. It clears the column late now rather than almost at
    once, but the principles are later siblings and opaque, so they simply paint
    over the tail of the photo's run as they arrive.
  */
  const photoX = useTransform(
    photo.linear,
    [0, 1],
    [`${slide ? PHOTO_CENTRE_OFFSET : 0}rem`, "0rem"],
    { ease: PHOTO_EASE },
  );

  return (
    <section className={cn("relative bg-[#0A1A1D] overflow-clip", className)}>
      <div className="relative mx-auto flex w-[min(240rem,100%)] flex-col items-center px-[3.625rem] pt-[5.375rem] pb-0 lg:h-[173.75rem] lg:px-0 lg:pt-[12.875rem]">
        {/* the verticals run on the mobile frame too, just tucked against the edges */}
        <DecorationLine
          className="left-[1rem] lg:left-[7.125rem]"
          orientation="vertical"
          style="light"
        />
        <DecorationLine
          className="right-[1rem] lg:right-[7rem]"
          orientation="vertical"
          style="light"
        />
        <DecorationLine
          className="hidden lg:block top-[39.875rem]"
          orientation="horizontal"
          style="light"
        />
        <DecorationLine
          className="hidden lg:block top-[166.5rem]"
          orientation="horizontal"
          style="light"
        />

        {/* header */}
        <div className="flex w-full flex-col items-start gap-[3rem] text-left lg:w-[133.75rem] lg:items-center lg:text-center">
          {data?.missionChip?.text && (
            <Chip
              image={data?.missionChip?.icon}
              text={data?.missionChip?.text}
            />
          )}

          {data?.missionTitle && (
            <div className="richtext richtext-center items-start text-left lg:items-center lg:text-center prose-p:font-geist prose-p:text-[4rem] prose-p:leading-[1.2] prose-p:tracking-[-0.02em] prose-p:text-white lg:prose-p:text-[6rem] prose-em:font-ivy prose-em:text-[4rem] lg:prose-em:text-[6.5rem] prose-em:tracking-normal prose-em:text-white/50">
              <PortableText
                value={data.missionTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/*
          photo + principles — the photo starts centred over the principles' half
          of the row and travels left out of it, so they are revealed rather than
          simply faded up. Only the photo is transformed: the principles hold their
          final layout the whole time and are just invisible until their turn.
        */}
        {/*
          The mobile frame rules off the header before the photo. The wrapper takes
          the viewport rather than the padded column because the rule is full-bleed
          and DecorationLine measures its containing block.
        */}
        <div className="relative mt-[4rem] h-[2px] w-screen shrink-0 lg:hidden">
          <DecorationLine
            className="top-0"
            orientation="horizontal"
            style="light"
          />
        </div>

        <div
          ref={rowRef}
          className="mt-[2.75rem] flex w-full flex-col gap-[2rem] lg:mt-[9.625rem] lg:w-[196rem] lg:flex-row lg:items-end lg:gap-[4.5rem] lg:p-[1.25rem]">
          {data?.missionImage?.src && (
            <MotionDiv
              style={{ opacity: photo.reveal, x: photoX }}
              /*
                Promoted: 32.8rem is the longest single travel on the page, and it
                is carrying the widest photo. Note this one is already composited
                for part of its run — the opacity beside it is under 1 while the
                photo fades up — so the promotion is really about holding that
                layer for the travel, which outlasts the fade by design.
              */
              transformTemplate={slide ? promoteLayer : undefined}
              className="flex flex-col items-start gap-[0.28rem] lg:h-[81.75rem] lg:w-[127.875rem] lg:gap-[0.75rem]">
              {data?.missionImageCaption && (
                <p className="m-0 rounded-[0.5rem] bg-[#D9F9B9] px-[0.28rem] py-[0.19rem] font-geistmono text-[1.5rem] leading-none text-black lg:px-[0.75rem] lg:py-[0.5rem] lg:text-[1.75rem]">
                  {data.missionImageCaption}
                </p>
              )}

              <div className="w-full rounded-[1.5rem] border-[0.125rem] border-[#D9F9B9]/50 p-[0.25rem] lg:flex-1 lg:p-[1rem]">
                <div className="relative aspect-[1007/610] w-full overflow-hidden rounded-[0.5rem] lg:aspect-auto lg:h-full">
                  <Image
                    className="absolute left-0 top-0 !h-full !w-full object-cover"
                    src={data.missionImage}
                    alt={data?.missionImageCaption || ""}
                    quality={100}
                  />
                </div>
              </div>
            </MotionDiv>
          )}

          {principles && principles.length > 0 && (
            <div className="flex flex-col gap-[1.125rem] lg:w-[60.75rem]">
              {principles.map((item, index) => (
                <Principle
                  key={index}
                  item={item}
                  progress={progress}
                  index={1 + index}
                  total={total}
                  reduceMotion={reduceMotion}
                />
              ))}
            </div>
          )}
        </div>

        {/* closing note */}
        {data?.missionText && (
          <div className="mt-[2.5rem] richtext richtext-center opacity-70 lg:mt-[4rem] lg:w-[70rem] prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-white">
            <PortableText
              value={data.missionText}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.missionSignature?.src && (
          /*
            Same bleed as the hero signature. The artwork is pure white here and the
            design's 40% is applied in CSS, since the dark ground needs the opposite
            ink to the light hero.
          */
          <div className="relative mt-[0.625rem] h-[10.75rem] w-[12.6rem] opacity-40 lg:mt-[3.25rem] lg:h-[12.75rem] lg:w-[22.25rem] lg:translate-x-[3.375rem]">
            <Image
              className="absolute -left-[0.87rem] -top-[3.21rem] !w-[25.88rem] max-w-none lg:-left-[1.04rem] lg:-top-[3.81rem] lg:!w-[30.7rem]"
              src={data.missionSignature}
              alt=""
              quality={100}
            />
          </div>
        )}

        {/* and rules off again under the signature, which is where the frame ends */}
        <div className="relative mt-[4rem] h-[2px] w-screen shrink-0 lg:hidden">
          <DecorationLine
            className="top-0"
            orientation="horizontal"
            style="light"
          />
        </div>
      </div>
    </section>
  );
}
